import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusBadge, EmptyState } from "../components/common";
import { QUOTE_STATUSES, REJECT_REASONS, eur, itDate } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Download, History, Pencil, Trash2 } from "lucide-react";

export default function Preventivi() {
  const { can } = useAuth();
  const canEdit = can("edit_quotes");
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ status: "bozza" });
  const [statusDlg, setStatusDlg] = useState(null);
  const [sForm, setSForm] = useState({});
  const [params, setParams] = useSearchParams();

  const load = async () => {
    const { data } = await api.get(`/quotes${filter ? `?status=${filter}` : ""}`);
    setItems(data);
    const eid = params.get("edit");
    if (eid) { const q = data.find((x) => x.id === eid); if (q) openEdit(q); setParams({}, { replace: true }); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => { if (document.body.style.pointerEvents === "none") document.body.style.pointerEvents = ""; }, 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  const openNew = () => { setEditId(null); setForm({ status: "bozza" }); setOpen(true); };
  const openEdit = (q) => {
    setEditId(q.id);
    setForm({ client_id: q.client_id, product: q.product, description: q.description, measures: q.measures, amount: q.amount, status: q.status, next_followup: q.next_followup });
    setOpen(true);
  };

  const save = async () => {
    if (!form.client_id) return toast.error("Seleziona il cliente");
    try {
      if (editId) {
        await api.put(`/quotes/${editId}`, form);
        toast.success("Preventivo aggiornato");
      } else {
        await api.post("/quotes", form);
        toast.success("Preventivo creato");
      }
      setOpen(false); setEditId(null); setForm({ status: "bozza" }); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito. I dati inseriti sono stati mantenuti.");
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Eliminare il preventivo ${q.code} di ${q.client_name}?\n\nL'operazione è definitiva e rimuove solo questo preventivo (il cliente e gli altri elementi NON vengono toccati).`)) return;
    try {
      await api.delete(`/quotes/${q.id}`);
      toast.success("Preventivo eliminato");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eliminazione non riuscita");
    }
  };

  const changeStatus = async () => {
    try {
      await api.post(`/quotes/${statusDlg.id}/status`, sForm);
      toast.success("Stato aggiornato"); setStatusDlg(null); setSForm({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Aggiornamento non riuscito"); }
  };

  return (
    <div>
      <PageHeader title="Preventivi" subtitle="Pipeline richieste, bozze e preventivi inviati">
        <a href={`${api.defaults.baseURL}/export/quotes.csv`} target="_blank" rel="noreferrer"><Button variant="outline"><Download size={16} className="mr-1" /> CSV</Button></a>
        {canEdit && <Button className="bg-brand hover:bg-[#A8472F] text-white" data-testid="btn-new-quote" onClick={openNew}><Plus size={16} className="mr-1" /> Nuovo preventivo</Button>}
      </PageHeader>

      <div className="flex gap-1.5 flex-wrap mb-5">
        <button onClick={() => setFilter("")} className={`text-xs px-3 py-1.5 rounded-full border ${!filter ? "bg-ink text-white" : "bg-white text-muted-foreground"}`}>Tutti</button>
        {QUOTE_STATUSES.map((s) => (
          <button key={s.value} data-testid={`filter-${s.value}`} onClick={() => setFilter(s.value)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === s.value ? "bg-ink text-white" : "bg-white text-muted-foreground"}`}>{s.label}</button>
        ))}
      </div>

      {items.length === 0 ? <EmptyState title="Nessun preventivo" /> : (
        <div className="space-y-2">
          {items.map((q) => (
            <div key={q.id} data-testid={`quote-row-${q.id}`} className="card-soft p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
              <div className="min-w-0">
                <p className="font-head font-bold text-sm">{q.code} <span className="text-muted-foreground font-normal">· {q.client_name}</span></p>
                <p className="text-sm text-muted-foreground truncate">{q.product} · {eur(q.amount)}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {q.first_sent_at ? `Inviato ${itDate(q.first_sent_at)}` : "Non ancora inviato"}
                  {q.next_followup ? ` · richiamo ${itDate(q.next_followup)}` : ""}
                  {q.reject_reason ? ` · motivo: ${q.reject_reason}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <StatusBadge list={QUOTE_STATUSES} value={q.status} />
                <Button size="sm" variant="outline" data-testid={`change-status-${q.id}`} onClick={() => { setStatusDlg(q); setSForm({ status: q.status }); }}><History size={14} className="mr-1" /> Stato</Button>
                {canEdit && <Button size="sm" variant="outline" data-testid={`edit-quote-${q.id}`} onClick={() => openEdit(q)}><Pencil size={14} className="mr-1" /> Modifica</Button>}
                {canEdit && <Button size="sm" variant="outline" data-testid={`delete-quote-${q.id}`} onClick={() => remove(q)} className="text-destructive border-destructive/40 hover:bg-red-50"><Trash2 size={14} /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New / edit quote */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="quote-dialog">
          <DialogHeader><DialogTitle className="font-head">{editId ? "Modifica preventivo" : "Nuovo preventivo"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Cliente *</Label>
              <Select value={form.client_id || ""} onValueChange={(v) => setForm({ ...form, client_id: v })} disabled={!!editId}>
                <SelectTrigger data-testid="quote-client"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>{clients.filter((c) => c.id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Prodotto</Label><Input data-testid="quote-product" value={form.product||""} onChange={(e)=>setForm({...form,product:e.target.value})} /></div>
            <div><Label>Descrizione</Label><Textarea value={form.description||""} onChange={(e)=>setForm({...form,description:e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Misure</Label><Input value={form.measures||""} onChange={(e)=>setForm({...form,measures:e.target.value})} /></div>
              <div><Label>Importo €</Label><Input type="number" data-testid="quote-amount" value={form.amount||""} onChange={(e)=>setForm({...form,amount:e.target.value})} /></div>
            </div>
            {!editId && (
              <div><Label>Stato iniziale</Label>
                <Select value={form.status} onValueChange={(v)=>setForm({...form,status:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>{QUOTE_STATUSES.map((s)=><SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Prossimo richiamo (data)</Label><Input type="date" value={form.next_followup||""} onChange={(e)=>setForm({...form,next_followup:e.target.value})} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Annulla</Button><Button className="bg-brand text-white" data-testid="quote-save" onClick={save}>Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change status */}
      <Dialog open={!!statusDlg} onOpenChange={() => setStatusDlg(null)}>
        <DialogContent data-testid="status-dialog">
          <DialogHeader><DialogTitle className="font-head">Cambia stato · {statusDlg?.code}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nuovo stato</Label>
              <Select value={sForm.status} onValueChange={(v)=>setSForm({...sForm,status:v})}>
                <SelectTrigger data-testid="new-status"><SelectValue /></SelectTrigger>
                <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>{QUOTE_STATUSES.map((s)=><SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {sForm.status === "rifiutato" && (
              <>
                <div><Label>Motivo del rifiuto</Label>
                  <Select value={sForm.reject_reason} onValueChange={(v)=>setSForm({...sForm,reject_reason:v})}>
                    <SelectTrigger data-testid="reject-reason"><SelectValue placeholder="Seleziona motivo" /></SelectTrigger>
                    <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>{REJECT_REASONS.map((r)=><SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Nota</Label><Textarea value={sForm.reject_note||""} onChange={(e)=>setSForm({...sForm,reject_note:e.target.value})} /></div>
              </>
            )}
            <div><Label>Prossimo richiamo (data)</Label><Input type="date" value={sForm.next_followup||""} onChange={(e)=>setSForm({...sForm,next_followup:e.target.value})} /></div>
            {statusDlg?.status_history?.length > 0 && (
              <div className="bg-accent rounded-lg p-3 text-xs space-y-1 max-h-32 overflow-y-auto">
                <p className="font-semibold">Storico stati</p>
                {statusDlg.status_history.map((h,i)=><p key={i} className="text-muted-foreground">{itDate(h.at)} → {h.status} {h.note && `(${h.note})`}</p>)}
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setStatusDlg(null)}>Annulla</Button><Button className="bg-brand text-white" data-testid="status-save" onClick={changeStatus}>Conferma</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
